import React, { useState } from 'react';
import {
  STRIPE_SANDBOX_TEST_CARD,
  resolvePayPalSandboxBuyer,
} from '../utils/paymentSandbox';

const CONTEXT_HINTS = {
  checkout: 'Sandbox = test credentials. Live = real charges.',
};

function copyText(value) {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
  navigator.clipboard.writeText(value).catch(() => {});
}

function SandboxCredentialRow({ label, value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="payment-mode-switch__cred-row">
      <span className="payment-mode-switch__cred-label">{label}</span>
      <code className="payment-mode-switch__cred-value">{value}</code>
      <button
        type="button"
        className="payment-mode-switch__copy-btn"
        onClick={handleCopy}
        aria-label={`Copy ${label}`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function StripeSandboxHints() {
  return (
    <div className="payment-mode-switch__hints payment-mode-switch__hints--stripe">
      <p className="payment-mode-switch__hints-title">Test card</p>
      <SandboxCredentialRow label="Card" value={STRIPE_SANDBOX_TEST_CARD.number} />
      <SandboxCredentialRow label="Expiry" value={STRIPE_SANDBOX_TEST_CARD.expiry} />
      <SandboxCredentialRow label="CVC" value={STRIPE_SANDBOX_TEST_CARD.cvc} />
      <p className="payment-mode-switch__hints-note">Name / ZIP: any. No real charge.</p>
    </div>
  );
}

function PayPalSandboxHints({ buyer }) {
  const email = buyer?.email || '';
  const password = buyer?.password || '';
  if (!email && !password) {
    return (
      <div className="payment-mode-switch__hints payment-mode-switch__hints--paypal">
        <p className="payment-mode-switch__hints-title">Sandbox buyer login</p>
        <p className="payment-mode-switch__hints-note">
          Set PAYPAL_SANDBOX_BUYER_EMAIL / PAYPAL_SANDBOX_BUYER_PASSWORD in server env.
        </p>
      </div>
    );
  }
  return (
    <div className="payment-mode-switch__hints payment-mode-switch__hints--paypal">
      <p className="payment-mode-switch__hints-title">Sandbox buyer login</p>
      {email && <SandboxCredentialRow label="Email" value={email} />}
      {password && <SandboxCredentialRow label="Password" value={password} />}
    </div>
  );
}

function ProviderModeSelect({
  id,
  label,
  icon,
  mode,
  defaultMode,
  available,
  onChange,
  disabled,
  showHints,
  provider,
  compact,
  paypalBuyer,
}) {
  const sandboxAvailable = Boolean(available?.sandbox);
  const liveAvailable = Boolean(available?.live);

  if (!sandboxAvailable && !liveAvailable) {
    return null;
  }

  const fallback = sandboxAvailable ? 'sandbox' : 'live';
  const effectiveMode =
    mode === 'live' || mode === 'sandbox'
      ? (mode === 'live' && !liveAvailable ? fallback : mode === 'sandbox' && !sandboxAvailable ? fallback : mode)
      : (defaultMode === 'live' && liveAvailable
        ? 'live'
        : defaultMode === 'sandbox' && sandboxAvailable
          ? 'sandbox'
          : fallback);
  const isLive = effectiveMode === 'live';
  const dualChoice = sandboxAvailable && liveAvailable;

  return (
    <div className={`payment-mode-switch__provider ${isLive ? 'payment-mode-switch__provider--live' : 'payment-mode-switch__provider--sandbox'}${compact ? ' payment-mode-switch__provider--compact' : ''}`}>
      <div className="payment-mode-switch__provider-header">
        <span className="payment-mode-switch__provider-label">
          {icon} <strong>{label}</strong>
          <span className="payment-mode-switch__provider-env">environment</span>
        </span>
        <span className={`payment-mode-switch__badge payment-mode-switch__badge--small ${isLive ? 'is-live' : 'is-sandbox'}`}>
          {isLive ? 'LIVE' : 'SANDBOX'}
        </span>
      </div>

      <div
        className={`payment-mode-switch__toggle${dualChoice ? '' : ' payment-mode-switch__toggle--single'}`}
        role="radiogroup"
        aria-label={`${label} payment environment`}
        id={id}
      >
        {sandboxAvailable && (
          <button
            type="button"
            role="radio"
            aria-checked={!isLive}
            className={`payment-mode-switch__toggle-btn${!isLive ? ' is-active is-sandbox' : ''}`}
            onClick={() => onChange('sandbox')}
            disabled={disabled || !sandboxAvailable}
          >
            <span className="payment-mode-switch__toggle-title">Sandbox</span>
            <span className="payment-mode-switch__toggle-sub">Test — no real money</span>
          </button>
        )}
        {liveAvailable && (
          <button
            type="button"
            role="radio"
            aria-checked={isLive}
            className={`payment-mode-switch__toggle-btn${isLive ? ' is-active is-live' : ''}`}
            onClick={() => onChange('live')}
            disabled={disabled || !liveAvailable}
          >
            <span className="payment-mode-switch__toggle-title">Live</span>
            <span className="payment-mode-switch__toggle-sub">Production charges</span>
          </button>
        )}
      </div>

      {defaultMode && defaultMode !== effectiveMode && (
        <p className="payment-mode-switch__override-note payment-mode-switch__override-note--inline">
          Session override (server default: {defaultMode})
        </p>
      )}

      {showHints && !isLive && provider === 'stripe' && <StripeSandboxHints />}
      {showHints && !isLive && provider === 'paypal' && <PayPalSandboxHints buyer={paypalBuyer} />}
      {showHints && isLive && (
        <p className="payment-mode-switch__live-warning">
          Real {label} account — charges are live.
        </p>
      )}
    </div>
  );
}

/**
 * Admin per-provider sandbox/live controls (checkout + Admin → Orders).
 * Pass `providers` to show only selected methods: ['stripe'], ['paypal'], or both.
 */
export default function PaymentModeSwitch({
  stripeMode,
  paypalMode,
  defaultStripeMode,
  defaultPayPalMode,
  availableProviderModes,
  onStripeChange,
  onPayPalChange,
  disabled = false,
  context = 'checkout',
  showSandboxHints = context === 'checkout',
  providers = null,
  paymentConfig = null,
}) {
  const stripeAvailable = availableProviderModes?.stripe;
  const paypalAvailable = availableProviderModes?.paypal;
  const paypalBuyer = resolvePayPalSandboxBuyer(paymentConfig);

  const wantStripe = !providers || providers.includes('stripe');
  const wantPaypal = !providers || providers.includes('paypal');

  const hasStripe = wantStripe && (stripeAvailable?.sandbox || stripeAvailable?.live);
  const hasPaypal = wantPaypal && (paypalAvailable?.sandbox || paypalAvailable?.live);

  if (!hasStripe && !hasPaypal) {
    return null;
  }

  const singleProvider = (hasStripe ? 1 : 0) + (hasPaypal ? 1 : 0) === 1;
  const title = hasStripe && !hasPaypal
    ? 'Stripe environment'
    : hasPaypal && !hasStripe
      ? 'PayPal environment'
      : 'Payment environments';

  return (
    <div className={`payment-mode-switch payment-mode-switch--split admin-payment-mode-select${singleProvider ? ' payment-mode-switch--single' : ''}`}>
      <div className="payment-mode-switch__header admin-payment-mode-select__header">
        <strong>{title}</strong>
      </div>
      {!singleProvider && (
        <p className="payment-mode-switch__hint admin-payment-mode-select__hint">
          {CONTEXT_HINTS[context] || CONTEXT_HINTS.checkout}
        </p>
      )}
      <div className={`payment-mode-switch__providers${singleProvider ? ' payment-mode-switch__providers--single' : ''}`}>
        {hasStripe && (
          <ProviderModeSelect
            id={`admin-stripe-mode-${context}`}
            label="Stripe"
            icon="💳"
            provider="stripe"
            mode={stripeMode}
            defaultMode={defaultStripeMode}
            available={stripeAvailable}
            onChange={onStripeChange}
            disabled={disabled}
            showHints={showSandboxHints}
            compact={singleProvider}
          />
        )}
        {hasPaypal && (
          <ProviderModeSelect
            id={`admin-paypal-mode-${context}`}
            label="PayPal"
            icon="🅿️"
            provider="paypal"
            mode={paypalMode}
            defaultMode={defaultPayPalMode}
            available={paypalAvailable}
            onChange={onPayPalChange}
            disabled={disabled}
            showHints={showSandboxHints}
            compact={singleProvider}
            paypalBuyer={paypalBuyer}
          />
        )}
      </div>
    </div>
  );
}
